import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    effect, ElementRef,
    HostListener,
    inject,
    input,
    signal,
    viewChild
} from '@angular/core';
import {WjGaugeModule} from '@mescius/wijmo.angular2.gauge';
import {WjCharts} from '../../lib/wjcharts';
import {ConfigMatrixApi, Portfolio, PortfolioResponse, PortfolioStatisticStatusEnum} from '../../../api-client-kt/src';
import {Alerts} from '../../lib/alerts';
import {Router} from '@angular/router';
import {Location, PercentPipe} from '@angular/common';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {sortBy, sumBy} from 'es-toolkit';
import {UniqueElementIdDirective} from '../../directives/unique-element-id.directive';
import {Tooltip} from '@fluentui/web-components';
import {RadialGauge} from '@mescius/wijmo.gauge';
import {Wijmo} from '../../lib/wjgrid-utils';

interface GaugeRange {
    min: number;
    max: number;
    color: string;
}

const SegStatusZero = Object.fromEntries(Object.values(PortfolioStatisticStatusEnum)
    .map(s => [s, 0]));


/**
 * Represents the portfolio card component within the application. This component
 * displays key portfolio data, such as statistical insights and progress indicators,
 * and provides functionality for navigation and interaction with portfolio details.
 */
@Component({
    selector: 'app-app-portfolio-card',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    standalone: true,
    imports: [
        WjGaugeModule,
        PercentPipe,
        UniqueElementIdDirective
    ],
    templateUrl: './app-portfolio-card.component.html',
    styleUrl: './app-portfolio-card.component.scss',
})
export class AppPortfolioCardComponent implements AfterViewInit {

    public readonly item = input.required<PortfolioResponse>();

    protected GaugePointerColor = WjCharts.GeneraliPrimaryColors1[2];
    protected GaugeMinMaxRanges: GaugeRange[] = [
        { min:  0, max:  33, color: WjCharts.GeneraliChartPalette[0] },
        { min: 33, max:  66, color: WjCharts.GeneraliChartPalette[1] },
        { min: 66, max: 100, color: WjCharts.GeneraliSecondaryColors2[4] }
    ];

    protected readonly plr = signal(0);
    protected readonly nlr = signal(0);
    protected readonly progress = signal(-1);
    protected readonly segmentStatusEntries = signal<Record<PortfolioStatisticStatusEnum, number>>({} as any);

    private readonly tt = viewChild.required<ElementRef<Tooltip>>('tt');
    private readonly rg = viewChild.required<RadialGauge>('g1');

    private readonly router = inject(Router);
    private readonly location = inject(Location);

    protected readonly shiftKey = signal(false);
    protected readonly ctrlKey = signal(false);

    private readonly pfData = inject(PortfolioDataContext);
    private readonly pfSegData = inject(PortfolioSegmentDataContext);

    private readonly configMatrixApi = inject(ConfigMatrixApi);

    protected readonly Wijmo = Wijmo;

    @HostListener('window:keydown', ['$event'])
    @HostListener('window:keyup', ['$event'])
    @HostListener('window:mousemove', ['$event'])
    protected updateKeyState(event: MouseEvent | KeyboardEvent) {
        event.stopPropagation();
        this.shiftKey.set(event.shiftKey);
        this.ctrlKey.set(event.ctrlKey);
    }

    constructor() {
        effect(() => this.loadDashboardData());
    }

    ngAfterViewInit() {
        // workaround for a fluent-tooltip-within-Angular conceptual problem:
        // it needs to be connected by element ID to the anchor element
        // however, at the instant, when the web component is connected to the DOM
        // (via connectedCallback()) the ID of the Angular element is not yet known
        // because it gets generated dynamically by Angular a little later
        // Hence, we need to call the connectCallback() manually after the element is rendered
        const tt = this.tt().nativeElement; // document.createElement('fluent-tooltip') as Tooltip;
        tt.connectedCallback();
    }

    async loadDashboardData(): Promise<void> {
        const id = this.item().portfolioModel.id!;
        const nlrRequest = this.configMatrixApi.getConfigMatrixValues({id, key: 'nlr'});
        const plrRequest = this.configMatrixApi.getConfigMatrixValues({id, key: 'plr'});
        const gnwpRequest = this.configMatrixApi.getConfigMatrixValues({id, key: 'gnwp'});

        const [nlrs, plrs, gnwps] = await Promise.all([nlrRequest, plrRequest, gnwpRequest]);

        const  numericCfgVal = (v: any) => v as number ?? 0;

        const weights: Record<string, Record<string, number>> = {};
        let totalGnep = 0;
        for (const gnwp of gnwps) {
            const lob = gnwp.lobKey!;
            const office = gnwp.officeKey!;
            if (!weights[lob]) {
                weights[lob] = {};
            }
            weights[lob][office] = numericCfgVal(gnwp.value);
            totalGnep += numericCfgVal(gnwp.value);
        }

        const plr = sumBy(plrs, p => numericCfgVal(p.value) * (weights[p.lobKey!][p.officeKey!] ?? 0)) / totalGnep;
        const nlr = sumBy(nlrs, n => numericCfgVal(n.value) * (weights[n.lobKey!][n.officeKey!] ?? 0)) / totalGnep;

        this.plr.set(plr);
        this.nlr.set(nlr);

        const segStatusEntries = this.item().statsByStatus.map(s => [s.status, s.count ?? 0]);
        const segStatusFromServer = Object.fromEntries(segStatusEntries);
        const segStatusAllStats = { ... SegStatusZero, ...segStatusFromServer };
        const segTotalCount = sumBy(this.item().statsByStatus, s => s.count ?? 0);
        const segStatusWeightTotal = Math.max(3 * segTotalCount, 1);
        const segStatus =
            0 * (segStatusAllStats[PortfolioStatisticStatusEnum.Open] ?? 0) +
            1 * (segStatusAllStats[PortfolioStatisticStatusEnum.InLocalReview] ?? 0) +
            2 * (segStatusAllStats[PortfolioStatisticStatusEnum.InGlobalReview] ?? 0) +
            3 * (segStatusAllStats[PortfolioStatisticStatusEnum.Approved] ?? 0);

        this.segmentStatusEntries.set(segStatusAllStats);
        this.progress.set(segStatus / segStatusWeightTotal * 100);
    }

    async navigate(event: PointerEvent, item: Portfolio) {
        // Open the portfolio in a new tab if the shift or ctrl key is pressed,
        // This is always allowed because it will not discard changes made to the portfolio
        if (event.shiftKey || event.ctrlKey) {
            const tree = this.router.createUrlTree(['app', 'pfm', item.id, 'members']);
            const serialized = this.router.serializeUrl(tree);
            const external = window.location.origin + this.location.prepareExternalUrl(serialized);
            window.open(external, '_blank')?.focus();
        } else {
            // Otherwise check if there are pending changes and prevent navigation if so
            if (this.pfSegData.isDirty()) {
                await Alerts.alertPopup({
                    body: 'There are pending changes. Please save or discard your changes before navigating.'
                });
            } else {
                await this.pfSegData.deactivatePortfolioAndSegment();
                await this.pfData.deactivatePortfolio();
                await this.router.navigate(['app', 'pfm', item.id, 'members']);
            }
        }
    }

    protected readonly PortfolioStatisticStatusEnum = PortfolioStatisticStatusEnum;
}
